import QuestionSequence from '@sleepstation/components-question-sequence'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useQuery } from 'urql'
import styled from 'styled-components'
import UserContext from '../../../../context/UserContext'
import LoadOverlay from '../../../shared/LoadOverlay'
import { QuestionnaireQuery } from '../../../../graphql/documents'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const EPWORTH_SLEEP_SCALE = `epworth`
const BUTTON_LIST = `buttonList`
const QUESTION = `question`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const transformOptions = (options) =>
  options.map((option) => ({
    label: option.value,
    value: option.key,
    score: option.score,
  }))

const transformStepObject = (step) => ({
  id: step.id,
  type: QUESTION,
  questionType: BUTTON_LIST,
  question: step.contents.nodes[0].value,
  options: transformOptions(step.options.nodes),
  nextStep: {
    default: step.defaultNextStepId,
  },
  data: {
    survey: EPWORTH_SLEEP_SCALE,
  },
})

const findNodeById = (nodes, id) => nodes.find((node) => node.id === id)

const orderSteps = ({ steps, firstStepId }) => {
  const orderedSteps = []
  const { nodes } = steps
  let nextStepId = firstStepId

  while (nextStepId) {
    const nextStep = findNodeById(nodes, nextStepId)
    orderedSteps.push(transformStepObject(nextStep))
    nextStepId = nextStep.defaultNextStepId
  }

  return orderedSteps
}

const determineChosenStepScore = (step, answerKey) =>
  step.options.find((option) => option.value === answerKey).score

const determineStepResponses = (userData) =>
  Object.keys(userData).map((key) => {
    const answerKey = userData[key].answer
    const { step } = userData[key]

    return {
      stepId: step.id,
      chosenOptionScore: determineChosenStepScore(step, answerKey),
      chosenOptionValue: answerKey,
    }
  })

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div`
  position: relative;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const EpworthQuestionnaire = ({ onSuccess, onProgress }) => {
  const { currentEnrolment } = useContext(UserContext)
  const [result] = useQuery({
    query: QuestionnaireQuery,
    variables: {
      key: `epworth`,
    },
  })

  const { data, error } = result

  const handleComplete = ({ userData }) => {
    const responseParams = {
      params: {
        questionnaireId: data.questionnaire.id,
        enrolmentId: currentEnrolment.id,
        stepResponsesAttributes: determineStepResponses(userData),
      },
    }

    onSuccess(responseParams)
  }

  const handleProgress = (progressData) => {
    const { isComplete } = progressData

    onProgress(progressData)

    if (isComplete) {
      handleComplete(progressData)
    }
  }

  return !data || error ? (
    <LoadOverlay />
  ) : (
    <Layout>
      <QuestionSequence
        steps={orderSteps(data.questionnaire)}
        onProgress={handleProgress}
      />
    </Layout>
  )
}

EpworthQuestionnaire.propTypes = {
  onSuccess: PropTypes.func,
  onProgress: PropTypes.func,
}

EpworthQuestionnaire.defaultProps = {
  onSuccess: () => {},
  onProgress: () => {},
}

export default EpworthQuestionnaire
